// ============================================
// Creator Autofill - Image Gen (ES6)
// 이미지 자동 생성 모달
// ============================================

import { showToast, showGlobalLoading, hideGlobalLoading } from '../../utils.js';
import { getCurrentUser } from '../../app/state.js';
import { ENDPOINTS } from '../api.js';
import { authFetch } from '../../shared/auth-fetch.js';
import {
  getImageModelList,
  isCacheValid,
  getCurrentEditorData, setCache
} from '../state.js';
import { addEditorImage, updateImageGenButtonState } from '../editor.js';
import { loadImageModelsData, openLibrarySheet, renderLibraryImages } from '../image-gallery.js';
import {
  renderModelCarousel,
  selectModelInCarousel,
  calculateAutofillCost,
  updateCostDisplay,
  openAutofillModalGeneric,
  closeAutofillModalGeneric
} from '../../shared/autofill-utils.js';
import { showLoadingWithRandomText, hideLoadingWithRandomText } from './handler.js';

// ============================================
// 모델 캐러셀
// ============================================

function renderImageGenModelCarousel() {
  const imageModelList = getImageModelList();
  const selectedModel = document.getElementById('imageGenSelectedModel')?.value || imageModelList[0]?.id || '';

  renderModelCarousel(
    'imageGenModelCarousel',
    imageModelList,
    selectedModel,
    'CreatorModule',
    'selectImageGenModel',
    'id',
    'image'
  );

  const input = document.getElementById('imageGenSelectedModel');
  if (input && !input.value && imageModelList.length > 0) {
    input.value = imageModelList[0].id;
  }

  updateAutoImageGenCost();
}

export function selectImageGenModel(modelId) {
  selectModelInCarousel('imageGenModelCarousel', modelId, 'imageGenSelectedModel');
  updateAutoImageGenCost();
}

function updateAutoImageGenCost() {
  const selectedModelId = document.getElementById('imageGenSelectedModel')?.value || '';
  const cost = calculateAutofillCost(0, true, selectedModelId, getImageModelList(), 'id', 1);
  updateCostDisplay('imageGenCost', cost);
}

// ============================================
// 모달 열기/닫기
// ============================================

export async function openImageGenModal() {
  if (!getCurrentUser()) {
    showToast('로그인이 필요합니다');
    return;
  }

  const charPrompt = document.getElementById('editorCharPrompt')?.value?.trim();
  if (!charPrompt) {
    showToast('점술가 프롬프트 작성 후 이용 가능해요');
    return;
  }

  const modal = document.getElementById('imageGenModal');
  if (!modal) return;

  const needsModelLoad = getImageModelList().length === 0 && !isCacheValid('imageModels');

  if (needsModelLoad) {
    showGlobalLoading();
    const loadingEl = document.querySelector('.global-loading-text');
    if (loadingEl) loadingEl.textContent = '모델 불러오는 중...';

    try {
      await loadImageModelsData();
    } catch (error) {
      console.error('모델 로드 실패:', error);
    } finally {
      hideGlobalLoading();
    }
  }

  renderImageGenModelCarousel();
  openAutofillModalGeneric('imageGenModal');
}

export function closeImageGenModal() {
  closeAutofillModalGeneric('imageGenModal');
}

// ============================================
// 이미지 생성 실행
// ============================================

export async function confirmImageGen() {
  closeImageGenModal();

  const btn = document.getElementById('editorImageGenBtn');
  if (btn) {
    btn.disabled = true;
  }

  const selectedModelId = document.getElementById('imageGenSelectedModel')?.value || '';

  const existingData = {
    name: document.getElementById('editorCharName')?.value?.trim() || '',
    gender: document.getElementById('editorCharGender')?.value || '',
    age: document.getElementById('editorCharAge')?.value?.trim() || '',
    relationship: document.getElementById('editorCharRelationship')?.value?.trim() || '',
    prompt: document.getElementById('editorCharPrompt')?.value?.trim() || '',
    worldPrompt: document.getElementById('editorWorldPrompt')?.value?.trim() || ''
  };

  showLoadingWithRandomText('image');

  try {
    const response = await authFetch(ENDPOINTS.autoFillCreation, {
      body: {
        existingData: existingData,
        emptyFields: [],
        needsImage: true,
        needsIntro: false,
        showThoughts: false,
        imageModelId: selectedModelId || null
      }
    });

    const result = await response.json();

    if (result.success && result.data?.imageUrl) {
      const currentEditorData = getCurrentEditorData();
      if (!currentEditorData.generatedImages) {
        currentEditorData.generatedImages = [];
      }
      currentEditorData.generatedImages.unshift({
        url: result.data.imageUrl,
        name: `imagegen_${Date.now()}.png`,
        createdAt: new Date().toISOString()
      });
      setCache('generatedImages', currentEditorData.generatedImages);
      renderLibraryImages();

      // 라이브러리 바텀시트 열기 (직접 적용 대신)
      openLibrarySheet();
      showToast('이미지가 생성되었습니다!');
    } else {
      const errorMsg = typeof result.error === 'string'
        ? result.error
        : (result.error?.message || '이미지 생성 실패');
      throw new Error(errorMsg);
    }
  } catch (error) {
    console.error('이미지 생성 오류:', error);
    showToast(error.message || '이미지 생성 중 오류가 발생했습니다');
  } finally {
    hideLoadingWithRandomText();
    updateImageGenButtonState();
  }
}
